import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Sidebar, Topbar } from './shared';
import { GROUPS, PAGES, SEARCH_OPTIONS, BASE_PATH, DEFAULT_SLUG } from './registry';
import './DesignSystem.css';

/**
 * Design System dashboard.
 *
 * Fixed grouped sidebar, a top bar with search and the brand switch, and a
 * routed content pane — one URL per component, so pages are linkable and
 * survive a reload.
 *
 * The notification provider is mounted globally in App.js, so live toast
 * demos work without a second provider here.
 */
const DesignSystemPage = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const scrollRef = useRef(null);
  const [brand, setBrand] = useState(
    () => document.documentElement.getAttribute('data-brand') || 'CLS',
  );
  const [menuOpen, setMenuOpen] = useState(false);

  // Keep <html data-brand> in step with React state. The old page only wrote
  // the attribute inside the click handler, so the two could disagree until
  // the first switch.
  useEffect(() => {
    document.documentElement.setAttribute('data-brand', brand);
  }, [brand]);

  // Freeze the content column while the mobile drawer is open. Locking
  // `document.body` would do nothing — the body no longer scrolls at all now
  // that `.ds-page__scroll` is the scroller.
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!menuOpen || !scroller) return undefined;
    const previous = scroller.style.overflowY;
    scroller.style.overflowY = 'hidden';
    return () => { scroller.style.overflowY = previous; };
  }, [menuOpen]);

  // The content column is the scroller now, so the browser no longer resets
  // the position on navigation — otherwise a long page would hand its scroll
  // offset to the next one.
  useEffect(() => {
    // Plain assignment rather than scrollTo(): every engine supports it, and
    // jsdom does not implement Element.scrollTo at all.
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [pathname]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const handleSelectPage = useCallback(
    (slug) => navigate(`${BASE_PATH}/${slug}`),
    [navigate],
  );

  return (
    <div className="ds-page">
      <Sidebar groups={GROUPS} open={menuOpen} onClose={closeMenu} basePath={BASE_PATH} />
      {menuOpen && (
        <button
          type="button"
          className="ds-page__sidebar-overlay"
          aria-label="Close navigation"
          onClick={closeMenu}
        />
      )}

      <div className="ds-page__main">
        <Topbar
          searchOptions={SEARCH_OPTIONS}
          onSelectPage={handleSelectPage}
          brand={brand}
          onBrandChange={setBrand}
          onOpenMenu={() => setMenuOpen(true)}
        />

        <div className="ds-page__scroll" ref={scrollRef}>
          <main className="ds-page__content">
            <Routes>
              <Route index element={<Navigate to={DEFAULT_SLUG} replace />} />
              {PAGES.map(({ slug, module, group }) => {
                const Page = module.default;
                return (
                  <Route
                    key={slug}
                    path={slug}
                    element={
                      <Page
                        brand={brand}
                        group={group}
                        groups={GROUPS}
                        pages={PAGES}
                        basePath={BASE_PATH}
                      />
                    }
                  />
                );
              })}
              <Route path="*" element={<Navigate to={DEFAULT_SLUG} replace />} />
            </Routes>
          </main>

          <footer className="typo-body ds-page__footer">
            Multi-brand design system — CLS &amp; ABC. Tokens generated from Figma via{' '}
            <code>npm run figma:sync</code>.
          </footer>
        </div>
      </div>
    </div>
  );
};

export default DesignSystemPage;
